import React, { useState, RefObject, useRef, useEffect } from 'react';
import { Box, Button, Grid, Tab, Tabs, Select, MenuItem, Typography, Dialog, Paper, DialogContent, Divider, Checkbox, DialogActions, IconButton, Alert, AlertTitle } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { deviceSettingsTabs } from '../../../store/configure/configuration/types';
import { TabContent } from './types';
import { switchConfigurationsHeadCells, switchConfigurationsTableSortableAccessors } from './table/head-cells';
import useStyles from './styles';
import { AppState } from '../../../store';
import AssetDeviceConfiguration from '../../../services/asset-device-configuration';
import SwitchConfiguration from '../../../services/switch-configuration';
import VirtualizedTable from '../../virtualized-table';


const DeviceSettings = () => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const selectedEntity = useSelector((state: AppState) => state.search.selectedEntity);
    const [logTab, setLogTab] = useState<string>('Start Mode');
    const [startModeData, setStartModeData] = useState<any[]>([]);
    const [tamperResistanceData, setTamperResistanceData] = useState<any[]>([]);
    const [switchesData, setSwitchesData] = useState<any[] | [] | null>(null);
    const [showNoCommandMessage, setShowNoCommandMessage] = useState<boolean>(false);
    const [disableDerateeData, setDisableDerateeData] = useState<any[]>([]);
    const [requestBody, setRequestBody] = useState<any>();
    const [isLoading, setIsLoading] = useState<boolean>();
    const tableContainer: RefObject<HTMLDivElement> = useRef(null);

    const { client, envVariables } = useSelector((state: AppState) => state.login)
    const assetDeviceConfiguration = new AssetDeviceConfiguration(
        client,
        envVariables.assetDeviceConfigV2BaseUrl,
    );
    const switchConfiguration = new SwitchConfiguration(
        client,
        envVariables.switchConfigurationsV1,
    );

    const tabContent: TabContent[] = [
        {
            title: deviceSettingsTabs.startMode,
            storageString: 'startMode',
        },
        {
            title: deviceSettingsTabs.tamperResistance,
            storageString: 'tamperResistance',
        },
        {
            title: deviceSettingsTabs.switches,
            storageString: 'switches',
        },
    ];

    const getResult = async () => {
        if (selectedEntity?.deviceId) {
            const requestBody = {
                make: selectedEntity.assetMake,
                serialNumber: selectedEntity.assetSerialNumber,
                deviceCharacterization: 'master',
                telematicsAssetId: selectedEntity.telematicsAssetId,
                deviceId: selectedEntity.deviceId,
                deviceType: selectedEntity.deviceType,
                deviceKey: selectedEntity.deviceKey,
            };
            setRequestBody(requestBody);
            try {
                const response = await assetDeviceConfiguration.getConfigurationForAsset(requestBody);

                const result = response.data;
                const startModeList = result && result.configs && result.configs[0]?.disableDerate?.disableDerateStartMode;
                const tamperList = result && result.configs && result.configs[0]?.disableDerate?.disableDerateTamperResistance;

                const startModeData: any = [{
                    desiredValue: startModeList?.desiredSetting?.machineStartMode,
                    currentValue: startModeList?.reportedSetting?.machineStartMode,
                    status: startModeList?.commands[0]?.status,
                    userId: startModeList?.commands[0]?.lastUpdatedByUserId,
                }];
                const tamperData: any = [{
                    desiredValue: tamperList?.desiredSetting?.tamperResistance,
                    currentValue: tamperList?.reportedSetting?.tamperResistance,
                    status: tamperList?.commands[0]?.status,
                    userId: tamperList?.commands[0]?.lastUpdatedByUserId,
                }];
                setStartModeData(startModeData);
                setTamperResistanceData(tamperData);
                setDisableDerateeData(startModeData);
                if (startModeData && startModeData.length > 0 && !startModeData[0]?.desiredValue) setShowNoCommandMessage(true);
            } catch (error: any) {
                if (error?.response?.status === 404) {
                    setStartModeData([]);
                    setTamperResistanceData([]);
                    setDisableDerateeData([]);
                }
            }
        }
    }

    const getSwitchResult = async () => {
        try {
            setIsLoading(true);
            const response = await switchConfiguration.getSwitchConfigurationForAsset(requestBody);
            const result = response && response?.data && response?.data?.configs && response?.data?.configs[0]?.switchConfigurations;
            const switchesDataList: any = result.map((item: any) => {
                const result = {
                    'switchNumber': item?.switchNumber,
                    'switchType': item?.switchType,
                    'name': item?.desiredSetting?.description,
                    'state': item?.desiredSetting?.state,
                    'monitorMode': item?.desiredSetting?.monitorMode,
                    'delayTime': item?.desiredSetting?.delayTime,
                    'status': item?.commands && item.commands[0]?.status,
                };
                return result;
            });

            setSwitchesData(switchesDataList);
            setIsLoading(false);
        } catch (error: any) {
            if (error?.response?.status === 404) {
                setSwitchesData([]);
            }
        }
    }

    useEffect(() => {
        getResult();
    }, [selectedEntity]);

    const handleSwitchTab = (newValue: any) => {
        setLogTab(newValue);
        setShowNoCommandMessage(false);
        if (newValue === 'Tamper Resistance') {
            setDisableDerateeData(tamperResistanceData);
            if (tamperResistanceData && tamperResistanceData.length > 0 && !tamperResistanceData[0]?.desiredValue) setShowNoCommandMessage(true);
        } else if (newValue === 'Start Mode') {
            setDisableDerateeData(startModeData);
            if (startModeData && startModeData.length > 0 && !startModeData[0]?.desiredValue) setShowNoCommandMessage(true);
        }
        else if (newValue === 'Switches') {
            getSwitchResult();
        }
    };

    return (
        <div>
            <Box px={3} pt={1} pb={0} display="flex" justifyContent="space-between">
                <Tabs
                    className={classes.tabs}
                    value={logTab}
                    onChange={(event, newValue) => handleSwitchTab(newValue)}
                    indicatorColor="secondary"
                    textColor="primary"
                >
                    {tabContent.map((tab: TabContent) => (
                        <Tab value={tab.title} label={tab.title} />
                    ))}
                </Tabs>
            </Box>
            <>
                {(logTab === 'Start Mode' || logTab === 'Tamper Resistance') &&
                    <>
                    {(showNoCommandMessage === true || disableDerateeData.length === 0) ? (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                mt={3}
                                overflow="auto"
                            >
                                <Typography variant="h6">No Disable Derate settings available for the device</Typography>
                            </Box>
                        ) :
                            disableDerateeData.length > 0 && disableDerateeData.map((item: any) => (
                                <Grid container xs={12} mt={2} ml={3}>
                                    <Grid item xs={6} md={3} className={classes.inputWrapper}>
                                        <Typography variant="h6">Desired Value:</Typography>
                                        <Typography > {item.desiredValue ? item.desiredValue : '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3} className={classes.inputWrapper}>
                                        <Typography variant="h6"> Current Value:</Typography>
                                        <Typography>{item.currentValue ? item.currentValue : '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3} className={classes.inputWrapper}>
                                        <Typography variant="h6"> Status:</Typography>
                                        <Typography >{item.status ? item.status : '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3} className={classes.inputWrapper}>
                                        <Typography variant="h6">User ID:</Typography>
                                        <Typography >{item.userId ? item.userId : '-'}</Typography>
                                    </Grid>
                                </Grid>
                            ))
                        }
                    </>
                }
                {logTab === 'Switches' &&
                    <>
                        {switchesData && switchesData.length > 0 ?
                            <div ref={tableContainer} className={classes.tableContainer}>
                                <VirtualizedTable
                                    sortableAccessors={switchConfigurationsTableSortableAccessors}
                                    columns={switchConfigurationsHeadCells}
                                    data={switchesData || []}
                                    itemCount={switchesData?.length || 0}
                                    tableContainer={tableContainer}
                                    loading={isLoading}
                                />
                            </div>
                            : <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                mt={3}
                                overflow="auto"
                            >
                                <Typography variant="h6">No Switches settings available for the device</Typography>
                            </Box>
                        }
                    </>
                }
            </>
        </div>
    );
}
export default DeviceSettings;